import React, { useState } from 'react'
import { Button, Radio, Space, Typography } from 'antd'
import { useTranslation } from 'lib/i18n'
import querystring from 'querystring'

import Link from 'components/Link/Link'
import { ModalCard } from 'components/UI/ModalCard/ModalCard'

import {
  DiscountProps,
  NodeProps,
  PriceProps,
  ProductChildrenProps,
  ProductSelectProps,
} from '../type'

import styles from './ProductSelect.module.css'

const { Title, Text } = Typography
const b2bChildProduct = 'BUSINESS_USE'

const getCheckoutLink = (
  profileSlug: string,
  profileProduct: NodeProps,
  isB2b: boolean,
) => {
  if (!profileProduct) return null
  const couponCode = profileProduct.discount?.couponCode

  const queryParams: { couponCode?: string; profileProductChild?: string } = {}
  if (couponCode) {
    queryParams.couponCode = couponCode
  }

  if (
    profileProduct.productChildren?.some(
      ({ product }) => product.productType === b2bChildProduct,
    ) &&
    isB2b
  ) {
    queryParams.profileProductChild = 'video-request-business-use'
  }

  const queryString = querystring.stringify(queryParams)

  let href = `/profile/${profileSlug}/product/${profileProduct.product.slug}`

  if (queryString) {
    href = `${href}?${queryString}`
  }
  return href
}

const ProfileProductPrice = ({
  discount,
  price,
}: {
  discount: DiscountProps
  price: PriceProps
}) => {
  const hasDiscount = !!discount?.discountedPrice

  return (
    <div className={styles.priceText} data-cy="profileProductPrice">
      <Text strong={!hasDiscount} delete={hasDiscount}>
        {price.formattedValue}
      </Text>
      {hasDiscount && (
        <Text strong>{discount.discountedPrice.formattedValue}</Text>
      )}
    </div>
  )
}

const ProfileProductRow = ({
  profileProduct,
  isB2b,
}: {
  profileProduct: NodeProps
  isB2b: boolean
}) => {
  const { product, productChildren } = profileProduct
  const b2bAddon =
    productChildren?.find(
      (child) => child.product.productType === b2bChildProduct,
    ) ?? {}

  const { discount, price } = profileProduct
  const {
    discount: b2bAddonDiscount,
    priceWithParent: b2bAddonPriceWithParent,
    product: b2bAddonProduct,
  }: Partial<ProductChildrenProps> = b2bAddon

  return (
    <Radio
      key={product.id}
      value={profileProduct}
      className={styles.radioOption}
      data-cy={`product-${product.slug}`}
    >
      <div className={styles.labelWrapper} data-cy="productRadioLabel">
        <Text strong>{product.name}</Text>
        <Text>
          {isB2b
            ? b2bAddonProduct?.description ?? product.description
            : product.description}
        </Text>
        {product.productType !== 'DONATION' && (
          <ProfileProductPrice
            discount={isB2b ? b2bAddonDiscount ?? discount : discount}
            price={isB2b ? b2bAddonPriceWithParent ?? price : price}
          />
        )}
      </div>
    </Radio>
  )
}

const ProductSelect = ({
  profileProducts,
  profileSlug,
  isB2b,
  selectedProductId,
  onClose,
}: ProductSelectProps) => {
  let [selectedProduct] = profileProducts
  if (selectedProductId) {
    selectedProduct =
      profileProducts.find((p) => p.product.id === selectedProductId) ||
      selectedProduct
  }

  const [chosenProduct, setChosenProduct] = useState(selectedProduct)
  const { t } = useTranslation()

  const title = isB2b
    ? t('ProductSelect.businessTitle', 'Business')
    : t('ProductSelect.personalTitle', 'Personal use')

  return (
    <ModalCard onClose={onClose}>
      <Title level={3} className={styles.title}>
        {title}
      </Title>

      <Radio.Group
        className={styles.radioGroup}
        onChange={(e) => setChosenProduct(e.target.value)}
        value={chosenProduct}
      >
        <Space direction="vertical">
          {profileProducts.map((profileProduct) => (
            <ProfileProductRow
              profileProduct={profileProduct}
              isB2b={isB2b}
              key={profileProduct.id}
            />
          ))}
        </Space>
      </Radio.Group>

      <Link href={getCheckoutLink(profileSlug, chosenProduct, isB2b)}>
        <Button
          className={styles.continueButton}
          type="primary"
          size="large"
          data-cy="productSelectModalContinueButton"
        >
          {t('CustomerDetails.ctaButton', 'Continue')}
        </Button>
      </Link>
    </ModalCard>
  )
}

export default ProductSelect
